import React, { useEffect, useState } from "react";
import { Button, type StatusOption } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import PdDatePicker from "~/shared/ui/Fields/PdDatePicker";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import { FILTER_BY_SELECT_OPTION } from "~/shared/hooks/useStaffFilterData/config";
import {
  FILTER_BY,
  useStaffFilterData,
} from "~/shared/hooks/useStaffFilterData/useStaffFilterData";
import { getSaveDateOrCurrentDate } from "~/shared/lib/getSaveDateOrCurrentDate/getSaveDateOrCurrentDate";
import { handlePopupScrollBottom } from "~/shared/lib/handlePopupScrollBottom/handlePopupScrollBottom";
import { Field } from "~/shared/ui/Field";
import { ISelectOption } from "~/shared/ui/Fields/types";
import {
  filterOptionByLabel,
  renderMaxTagPlaceholder,
} from "~/shared/ui/Select/lib";

import { getDefaultValues, TTrainingFilter } from "./model/config";

import s from "./StaffTrainingFilter.less";

interface IStaffTrainingFilterProps {
  onSubmit?: (values: TTrainingFilter) => void;
  onReset?: () => void;
}

const StaffTrainingFilter = ({
  onSubmit,
  onReset,
}: IStaffTrainingFilterProps) => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const isRu = language === "ru" || language === null;
  const settedFilter = useURLParamsGetter<TTrainingFilter>();
  const defaultValues = getDefaultValues(isRu);
  const combineDefaultValues = {
    ...defaultValues,
    ...settedFilter,
    ...{ period: getSaveDateOrCurrentDate(settedFilter.period) },
  } as TTrainingFilter;

  const { control, handleSubmit, setValue, reset } = useForm<TTrainingFilter>({
    defaultValues: combineDefaultValues,
  });

  useEffect(() => {
    onSubmit(combineDefaultValues);
  }, []);

  const {
    filterBySelectOptionsMap,
    positionsSelectOptionsListTraining,
    dealersSelectMethods,
    coursesSelectMethods,
  } = useStaffFilterData();

  const [currentFilterBy, setCurrentFilterBy] = useState(
    combineDefaultValues.filterBy.value,
  );

  const onFilterByChange = (value: ISelectOption) => {
    setCurrentFilterBy(value.value as keyof typeof FILTER_BY);
    setValue("filterItems", []);
  };

  const isDealersPick = currentFilterBy === FILTER_BY.DEALER;
  const filterItemsSelectOptions = filterBySelectOptionsMap[
    currentFilterBy
  ] as ISelectOption[];

  const handleReset = () => {
    if (onReset) return onReset();
    reset(getDefaultValues(isRu));
    setCurrentFilterBy(defaultValues.filterBy.value);
  };

  return (
    <form
      className={s.form}
      onSubmit={handleSubmit((values) => onSubmit?.(values))}
    >
      <div className={s.fields}>
        <div className={s.rowMain}>
          <Field
            type="select"
            name="filterBy"
            control={control}
            label="Отфильтровать"
            labelInValue
            options={FILTER_BY_SELECT_OPTION as StatusOption[]}
            onChange={(value) => onFilterByChange(value as ISelectOption)}
          />
          <Field
            type="select"
            name="filterItems"
            control={control}
            label="Значения"
            mode="multiple"
            labelInValue
            showSearch
            options={filterItemsSelectOptions as StatusOption[]}
            filterOption={isDealersPick ? false : filterOptionByLabel}
            onSearch={
              isDealersPick ? dealersSelectMethods.searchDealer : undefined
            }
            onOpenChange={
              isDealersPick
                ? (open) => open && dealersSelectMethods.searchDealer("")
                : undefined
            }
            onPopupScroll={
              isDealersPick
                ? handlePopupScrollBottom(
                    dealersSelectMethods.onScrollBottomDealers,
                  )
                : undefined
            }
            maxTagCount="responsive"
            maxTagPlaceholder={renderMaxTagPlaceholder}
          />
          <div className={s.fieldPeriod}>
            <span>Период</span>
            <PdDatePicker
              name="period"
              control={control}
              dateFormat="dd.MM.yyyy"
            />
          </div>
        </div>
        <div className={s.rowSecondary}>
          <Field
            type="select"
            name="filterExpectations"
            control={control}
            label="Должность"
            mode="multiple"
            labelInValue
            showSearch
            options={positionsSelectOptionsListTraining as StatusOption[]}
            filterOption={filterOptionByLabel}
            maxTagCount="responsive"
            maxTagPlaceholder={renderMaxTagPlaceholder}
          />
          <Field
            type="select"
            name="filterCourse"
            control={control}
            label="Курс"
            mode="multiple"
            labelInValue
            showSearch
            options={coursesSelectMethods.courses}
            filterOption={false}
            onSearch={coursesSelectMethods.searchCourses}
            onOpenChange={(open) =>
              open && coursesSelectMethods.searchCourses("")
            }
            onPopupScroll={handlePopupScrollBottom(
              coursesSelectMethods.onScrollBottomCourses,
            )}
            maxTagCount="responsive"
            maxTagPlaceholder={renderMaxTagPlaceholder}
          />
        </div>
      </div>
      <div className={s.buttons}>
        <Button
          onClick={handleReset}
          size="middle"
          color="secondary"
          variant="text"
          htmlType="button"
        >
          {t("ui.filter_reset")}
        </Button>
        <Button size="middle" color="secondary" htmlType="submit">
          {t("ui.filter_show")}
        </Button>
      </div>
    </form>
  );
};

export default StaffTrainingFilter;
